<template>
  <b-card class="my-1" border-variant="0" body-class="p-0">
    <WRow>
      <template v-for="field in donorDataFormSchemaArray" :key="field.name">
        <WCol v-if="field.ifCondition" :cols="field.cols" :md="field.md" :class="field.class">
          <component
            v-if="field.mask"
            v-bind="field.props"
            v-mask="field.mask"
            :is="field.component"
            :name="field.name"
          />
          <component v-else v-bind="field.props" :is="field.component" :name="field.name">
            <template v-for="(slot, slotName) in field.slots" :key="slotName" #[slotName]>
              <div v-html="slot"></div>
            </template>
          </component>
          <div v-if="field?.append">
            <component :is="field.append"></component>
          </div>
        </WCol>
      </template>
    </WRow>
  </b-card>
</template>

<script setup lang="ts">
import DonorPhoneDescription from '@/components/donorDataPage/DonorPhoneDescription.vue'
import FormCheckbox from '@/components/widgets/forms/FormCheckbox.vue'
import FormHeader from '@/components/widgets/forms/FormHeader.vue'
import FormInput from '@/components/widgets/forms/FormInput.vue'
import { useSwdBreakpoints } from '@/composables'
import useTransactionDataStore from '@/stores/transactionData'
import { PayerType, PaymentType, type FormInputType } from '@/types'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FormInfo from '../widgets/forms/FormInfo.vue'

const phoneMask = {
  mask: 'FFF FFF FFF FFF FFF FFF FF',
  tokens: {
    F: {
      pattern: /\+|[0-9]/
    }
  }
}

const nipMask = {
  mask: 'FFFFFFFFFF',
  tokens: {
    F: {
      pattern: /\+|[0-9]/
    }
  }
}

const transactionDataStore = useTransactionDataStore()
const { paymentType, transactionData } = storeToRefs(transactionDataStore)

const isAnonymousType = computed<boolean>(() => {
  return [PaymentType.fundraising, PaymentType.fundraisingDonate].includes(paymentType.value)
})

const showAnonymousCompanyCheckbox = computed<boolean>(() => {
  return transactionData.value?.payerType === PayerType.company && isAnonymousType.value
})

const showAnonymousPrivateCheckbox = computed<boolean>(() => {
  return transactionData.value?.payerType === PayerType.person && isAnonymousType.value
})

const isFirmPayerType = computed<boolean>(() => {
  return transactionData.value?.payerType === PayerType.company
})

const { t } = useI18n()

const textType: FormInputType = 'text'
const telType: FormInputType = 'tel'

const breakpoints = useSwdBreakpoints()

const mdAndLarger = breakpoints.greaterOrEqual('md')

// @TODO: I think this should be moved to a separate file, typed, and components should be imported dynamically
// We can think about it later, when doing the dynamic forms
const donorDataFormSchemaArray = computed(() => [
  {
    name: 'payerType',
    component: FormCheckbox,
    props: {
      label: t('donorForm.form.payerType'),
      trueValue: PayerType.company,
      falseValue: PayerType.person
    },
    cols: 12,
    md: 12,
    ifCondition: true,
    class: 'my-2'
  },
  {
    name: 'obligatoryFieldsDescription',
    component: FormInfo,
    slots: {
      default: `<span class="text-danger">*</span>${t('donorForm.form.obligatoryFieldsDescription')}`
    },
    ifCondition: true,
    cols: 12,
    class: 'my-2'
  },
  {
    component: FormHeader,
    props: {
      text: t('donorForm.form.companyData')
    },
    cols: 12,
    class: 'my-2',
    ifCondition: isFirmPayerType.value
  },
  {
    name: 'companyName',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.form.companyName'),
      rules: 'required|regularField|max:180'
    },
    cols: 12,
    md: 6,
    ifCondition: isFirmPayerType.value,
    class: 'my-2'
  },
  {
    name: 'anonymous',
    component: FormCheckbox,
    props: {
      label: t('donorForm.form.anonymousLabelCompany')
    },
    cols: 12,
    ifCondition: showAnonymousCompanyCheckbox.value && !mdAndLarger.value,
    class: 'd-my-2'
  },
  {
    name: 'nipNumber',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.form.nipNumber'),
      rules: 'required|numeric|length:10'
    },
    mask: nipMask,
    inputmode: 'numeric',
    cols: 12,
    md: 6,
    ifCondition: isFirmPayerType.value,
    class: 'my-2'
  },
  {
    name: 'anonymous',
    component: FormCheckbox,
    props: {
      label: t('donorForm.form.anonymousLabelCompany'),
      class: 'w-50'
    },
    md: 12,
    ifCondition: showAnonymousCompanyCheckbox.value && mdAndLarger.value,
    class: 'my-2'
  },
  {
    name: 'address.address',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.addressForm.address'),
      rules: 'required|regularField|max:256'
    },
    cols: 12,
    md: 12,
    ifCondition: isFirmPayerType.value,
    class: 'my-2'
  },
  {
    name: 'address.postalCode',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.addressForm.postalCode'),
      rules: 'required|postalCode'
    },
    mask: '##-###',
    cols: 6,
    md: 6,
    ifCondition: isFirmPayerType.value,
    class: 'my-2'
  },
  {
    name: 'address.city',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.addressForm.city'),
      rules: 'required|city'
    },
    cols: 6,
    md: 6,
    ifCondition: isFirmPayerType.value,
    class: 'my-2'
  },
  {
    component: FormHeader,
    props: {
      text: t('donorForm.form.personalData')
    },
    cols: 12,
    class: 'my-2',
    ifCondition: isFirmPayerType.value
  },
  {
    name: 'donor.firstName',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.form.firstName'),
      rules: 'required|excludeSpecial|max:180'
    },
    cols: 12,
    md: 6,
    ifCondition: true,
    class: 'my-2'
  },
  {
    name: 'anonymous',
    component: FormCheckbox,
    props: {
      label: t('donorForm.form.anonymousLabel')
    },
    cols: 12,
    ifCondition: showAnonymousPrivateCheckbox.value && !mdAndLarger.value,
    class: 'my-2'
  },
  {
    name: 'donor.lastName',
    component: FormInput,
    props: {
      type: textType,
      placeholder: t('donorForm.form.lastName'),
      rules: 'required|excludeSpecial|max:180'
    },
    cols: 12,
    md: 6,
    ifCondition: true,
    class: 'my-2'
  },
  {
    name: 'anonymous',
    component: FormCheckbox,
    props: {
      label: t('donorForm.form.anonymousLabel'),
      class: 'w-50'
    },
    cols: 12,
    ifCondition: showAnonymousPrivateCheckbox.value && mdAndLarger.value,
    class: 'my-2'
  },
  {
    name: 'donor.email',
    component: FormInput,
    props: {
      type: 'email',
      placeholder: t('donorForm.form.email'),
      rules: 'required|email|max:180'
    },
    cols: 12,
    md: 6,
    ifCondition: true,
    class: 'my-2'
  },
  {
    name: 'donor.phoneNumber',
    component: FormInput,
    props: {
      type: telType,
      placeholder: t('donorForm.form.phone'),
      rules: 'phoneNumber'
    },
    mask: phoneMask,
    cols: 12,
    md: 6,
    ifCondition: true,
    class: 'my-2',
    append: DonorPhoneDescription
  }
])
</script>
